import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useStaleGuard } from '~/utils';
import { defineStore } from 'pinia';

function uniqueShortVideosByCommercialId(items: ShortVideo[]): ShortVideo[] {
	const seenCommercialIds = new Set<string>();

	return items.filter(item => {
		const commercialId = item.property.commercialId;
		if (seenCommercialIds.has(commercialId)) return false;

		seenCommercialIds.add(commercialId);
		return true;
	});
}

export const useShortsFeedStore = defineStore('shortsFeed', () => {
	const videos = shallowRef<ShortVideo[]>([]);
	const isLoading = ref(false);
	const isPreloaded = ref(false);
	const hasMore = ref(true);
	const currentPage = ref(1);
	const activeFilters = shallowRef<ShortsFilters>({});
	const allWatched = ref(false);
	const totalAvailable = ref(0);
	const recommendationCountVideos = ref<number | undefined>(undefined);
	const recommendationCountPhotos = ref<number | undefined>(undefined);

	const staleGuard = useStaleGuard();

	function setVideos(items: ShortVideo[]) {
		videos.value = uniqueShortVideosByCommercialId(items);
	}

	function appendVideos(items: ShortVideo[]) {
		videos.value = uniqueShortVideosByCommercialId([...videos.value, ...items]);
	}

	function remainingAfter(commercialId: string) {
		const idx = videos.value.findIndex(
			v => v.property.commercialId === commercialId
		);
		if (idx === -1) {
			return {
				videos: recommendationCountVideos.value,
				photos: recommendationCountPhotos.value
			};
		}

		const watchedVideos = videos.value
			.slice(0, idx + 1)
			.filter(v => Boolean(v.videoUrl)).length;
		const watchedPhotos = idx + 1 - watchedVideos;

		return {
			videos:
				recommendationCountVideos.value !== undefined
					? Math.max(0, recommendationCountVideos.value - watchedVideos)
					: undefined,
			photos:
				recommendationCountPhotos.value !== undefined
					? Math.max(0, recommendationCountPhotos.value - watchedPhotos)
					: undefined
		};
	}

	function reset() {
		videos.value = [];
		isLoading.value = false;
		isPreloaded.value = false;
		hasMore.value = true;
		currentPage.value = 1;
		activeFilters.value = {};
		allWatched.value = false;
		totalAvailable.value = 0;
		recommendationCountVideos.value = undefined;
		recommendationCountPhotos.value = undefined;
		staleGuard.next();
	}

	return {
		videos,
		isLoading,
		isPreloaded,
		hasMore,
		currentPage,
		activeFilters,
		allWatched,
		totalAvailable,
		recommendationCountVideos,
		recommendationCountPhotos,
		guardNext: staleGuard.next,
		guardIsStale: staleGuard.isStale,
		setVideos,
		appendVideos,
		reset,
		remainingAfter
	};
});
